import React, {
	Dispatch,
	ReactNode,
	SetStateAction,
	useEffect,
	useState,
} from 'react';
import clsx from 'clsx';
import { CircleAlert, X } from 'lucide-react';

import { Button, Icon, Typography } from '@/components/common';
import CustomInputOtp from '@/components/common/custom-input-otp/CustomInputOtp';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';

interface Props {
	title: string;
	description: string | ReactNode;
	isOpen: boolean;
	sentCodeAgain: () => void;
	error: string | React.ReactNode;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	setErrorVerification: Dispatch<SetStateAction<string | React.ReactNode>>;
	validateOtpCode: (code: string) => void;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	numDigits: number;
}

export const ModalVerification: React.FC<Props> = ({
	description,
	error,
	isOpen,
	setIsOpen,
	sentCodeAgain,
	setErrorVerification,
	setIsModalOpen,
	title,
	validateOtpCode,
	numDigits,
}) => {
	const { Modal, show, hide } = useModal();
	const [code, setCode] = useState<string>('');

	useEffect(() => {
		if (isOpen) {
			show();
		} else {
			hide();
		}
	}, [isOpen]);

	const handleChangeCode = (code: string): void => {
		setErrorVerification('');
		setCode(code);
	};

	return (
		<Modal className="" onClose={() => setIsOpen(false)}>
			<div className="w-full md:w-[532px] flex flex-col items-center space-y-6">
				<div id="header" className="space-y-3 w-full relative">
					<div className="flex items-center justify-center">
						<Icon src={Icons.bigGreenCheck} className="!w-16" />
						<Typography type="semibold-20">{title}</Typography>
						<button
							className="absolute right-6 top-2"
							onClick={() => setIsModalOpen(false)}
						>
							<X />
						</button>
					</div>
					<div className="w-full">
						{typeof description === 'string' ? (
							<Typography type="regular-16" className="opacity-60">
								{description}
							</Typography>
						) : (
							description
						)}
					</div>
				</div>
				<div
					id="content"
					className="w-full flex flex-col items-center justify-center space-y-4 "
				>
					<CustomInputOtp
						length={numDigits}
						onChange={handleChangeCode}
						classNameSlot={clsx({
							'ring-1 ring-red-600': error,
						})}
					/>
					{error && (
						<div className="flex items-center gap-2">
							<CircleAlert className="text-red-600 !h-5 !w-5" />
							<Typography type="regular-12" className="text-red-600 text-left">
								{error}
							</Typography>
						</div>
					)}
					<div className="flex items-center justify-center gap-4">
						<Typography type="regular-16" text="Didn’t receive the code?" />
						<button type="button" onClick={sentCodeAgain}>
							<Typography
								type="semibold-16"
								className="text-purple-150"
								text="Resend verification code"
							/>
						</button>
					</div>
				</div>
				<div id="footer">
					<Button
						size="medium"
						disabled={!code || code.length < numDigits}
						decoration="primary"
						onClick={() => validateOtpCode(code)}
					>
						Verify
					</Button>
				</div>
			</div>
		</Modal>
	);
};
